import { FC, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { format, parseISO } from 'date-fns';
import { AuthSession, AccountContext } from 'auth/accountProvider';
import {
  buildCpiQueryParams,
  dashboardQueryKey, dashboardImpl,
  emissionsByCustomerQueryKey, emissionsByCustomerImpl,
  emissionsByTocQueryKey, emissionsByTocImpl,
  emissionsByMonthQueryKey, emissionsByMonthImpl,
  intensityPerTonQueryKey, intensityPerTonImpl,
  intensityPerTonKmQueryKey, intensityPerTonKmImpl,
  intensityByModalityQueryKey, intensityByModalityImpl,
  intensityByVehicleQueryKey, intensityByVehicleImpl,
  intensityByOrderQueryKey, intensityByOrderImpl,
  errorDistributionQueryKey, errorDistributionImpl,
  shipmentsByCustomerQueryKey, shipmentsByCustomerImpl,
  weightByModalityQueryKey, weightByModalityImpl,
  emissionSourceDistributionQueryKey, emissionSourceDistributionImpl,
  emissionsByFuelQueryKey, emissionsByFuelImpl,
  fuelConsumptionQueryKey, fuelConsumptionImpl,
  dataQualityByClientQueryKey, dataQualityByClientImpl,
  tripsQueryKey, tripsImpl,
  type CpiQueryParams, type CpiAuth, type CpiTripsPageParams,
} from 'lib/cpiQueries';
import CpiSummaryCards from './CpiSummaryCards';
import CpiEmissionsMatrix from './CpiEmissionsMatrix';
import ViewSwitch from './ViewSwitch';
import useStuckSentinel from '../../hooks/useStuckSentinel';
import ErrorDistributionChart from './ErrorDistributionChart';
import EmissionSourceDistributionChart from './EmissionSourceDistributionChart';
import EmissionsByCustomerChart from './EmissionsByCustomerChart';
import EmissionsByTocChart from './EmissionsByTocChart';
import EmissionsByMonthChart from './EmissionsByMonthChart';
import EmissionsByFuelChart from './EmissionsByFuelChart';
import EmissionsByRegionMap from './EmissionsByRegionMap';
import ChartErrorBoundary from './ChartErrorBoundary';
import { useRegionEmissions } from './regionMapData';

import WeightByModalityChart from './WeightByModalityChart';
import ShipmentsByCustomerChart from './ShipmentsByCustomerChart';
import DataQualityByClientChart from './DataQualityByClientChart';
import FuelConsumptionTable from './FuelConsumptionTable';
import AttributedFuelTable from './AttributedFuelTable';
import FuelConsumptionChart from './FuelConsumptionChart';
import IntensityPerTonMonthlyChart from './IntensityPerTonMonthlyChart';
import IntensityPerTonKmMonthlyChart from './IntensityPerTonKmMonthlyChart';
import IntensityByModalityChart from './IntensityByModalityChart';
import IntensityByVehicleChart from './IntensityByVehicleChart';
import IntensityByOrderChart from './IntensityByOrderChart';
import CpiTripsTable from './CpiTripsTable';
import { ytdRange } from '../lpiReports/monthRange';
import { EMISSIONS_MAP_ANCHOR, EMISSIONS_MAP_SUBTAB } from './anchors';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import MultiSelectDropdown from '../lpiReports/MultiSelectDropdown';
import { EmissionsView, DashboardSubTab, EmissionSourceDistributionResponse, EmissionsByMonthResponse, WeightByModalityResponse, IntensityPerTonMonthlyResponse, IntensityPerTonKmMonthlyResponse, IntensityByModalityResponse, IntensityByVehicleResponse, IntensityByOrderResponse, AdvancedFilters, EMPTY_ADVANCED_FILTERS } from './types';
import AdvancedFilterDrawer from './AdvancedFilterDrawer';
import FilterPills from './FilterPills';
import { ClientListResponse } from '../lpiReports/types';
import { exportCpiToPpt, CpiPptExportData } from './export/cpiPptExport';
import './CpiReports.scss';

/**
 * Empty filter arrays must not travel as query parameters. An empty `clientId=`
 * reads as a real filter to the request schema rather than as "no filter".
 */
const nonEmpty = (v?: string[]): string[] | undefined =>
  v && v.length > 0 ? v : undefined;

// Helper to parse date string to Date object (handles YYYY-MM-DD format properly)
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  // Use parseISO for consistent parsing of YYYY-MM-DD format
  const date = parseISO(dateStr);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Helper to format Date to string (YYYY-MM-DD) in local timezone
const formatDateToString = (date: Date | null): string => {
  if (!date) return '';
  // Use date-fns format to avoid timezone issues (unlike toISOString which converts to UTC)
  return format(date, 'yyyy-MM-dd');
};

// Helper to safely get session tokens with validation
interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

const getSessionTokens = async (
  getSession: () => Promise<AuthSession>
): Promise<SessionTokens> => {
  const session = await getSession();

  if (!session) {
    throw new Error('Session expired. Please log in again.');
  }

  const accessToken = session.getAccessToken?.();
  const idToken = session.getIdToken?.();

  if (!accessToken || !idToken) {
    throw new Error('Invalid session. Please log in again.');
  }

  const jwtToken = accessToken.getJwtToken?.();
  const sessionId = idToken.payload?.sessionId;

  if (!jwtToken || !sessionId) {
    throw new Error('Session tokens missing. Please log in again.');
  }

  return { jwtToken, sessionId };
};

// Run async tasks with bounded parallelism. Used by PPT export to lazy-fetch
// missing data without firing all 8 endpoints simultaneously, which previously
// could OOM the data processor on year-long ranges.
async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (cursor < tasks.length) {
      const i = cursor;
      cursor += 1;
      // Sequential within a worker is intentional — parallelism comes from
      // multiple workers running this loop at once.
      // eslint-disable-next-line no-await-in-loop
      results[i] = await tasks[i]();
    }
  });
  await Promise.all(workers);
  return results;
}

const CpiReports: FC = () => {
  const { t } = useTranslation();
  const { getSession, userInfo } = useContext(AccountContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Emissions view mode. Trip details used to be a top-level tab; it is now the
  // list view of the Emissions sub-tab, so its state lives here and rides in
  // the URL as ?view=list.
  const [emissionsView, setEmissionsView] = useState<EmissionsView>(
    () => (searchParams.get('view') === 'list' ? 'list' : 'report')
  );
  /**
   * Sub-tab, seeded from ?subtab= so a view is linkable — the same pattern the
   * top-level tab and the list/report view already use.
   *
   * Without this the Overview's "Open full map" could not work at all: the map
   * is rendered under Operational and this page opens on Emissions, so the
   * fragment pointed at an element that did not exist.
   */
  const [dashboardSubTab, setDashboardSubTab] = useState<DashboardSubTab>(() => {
    const requested = searchParams.get('subtab');
    return requested === 'operational' || requested === 'energy' ? requested : 'emissions';
  });
  // Trip Details — the Emissions sub-tab's list view. It shows the trips table
  // instead of the matrix and charts, so it loads and renders on different
  // terms from the rest of the dashboard panel.
  const isTripsListView = dashboardSubTab === 'emissions' && emissionsView === 'list';
  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showNoDataMessage, setShowNoDataMessage] = useState<boolean>(false);

  // Condenses the sticky filter bar once it pins to the top of the viewport.
  const { sentinelRef, isStuck } = useStuckSentinel();

  // Trips pagination state (data/loading/loaded come from tripsQuery below)
  const [tripsCurrentPage, setTripsCurrentPage] = useState<number>(1);
  const [tripsItemsPerPage, setTripsItemsPerPage] = useState<number>(10);

  // Export state
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);
  const [isExportingPpt, setIsExportingPpt] = useState<boolean>(false);

  /**
   * Scroll to the emissions map when arrived at by its fragment.
   *
   * A bare href="#emissions-map" cannot work: the browser resolves the fragment
   * on navigation, when this page has rendered its skeleton but not the map.
   *
   * Nor is "scroll as soon as the element exists" enough, which is how this
   * first shipped. The anchor appears early, while the matrix and five chart
   * grids ABOVE it are still loading; each one that arrives grows the page and
   * pushes the map further down, so the scroll lands somewhere in the middle of
   * the charts and the map is nowhere in sight.
   *
   * So it waits for the layout to settle: the element's offset has to come back
   * unchanged twice running before scrolling. A hard stop keeps a tenant whose
   * map never renders from polling for the page's lifetime.
   */
  const scrolledToMap = useRef(false);
  useEffect(() => {
    if (scrolledToMap.current) return undefined;
    if (window.location.hash !== `#${EMISSIONS_MAP_ANCHOR}`) return undefined;

    const started = Date.now();
    let lastTop: number | null = null;
    const timer = window.setInterval(() => {
      const el = document.getElementById(EMISSIONS_MAP_ANCHOR);
      const giveUp = Date.now() - started > 20_000;

      if (!el) {
        if (giveUp) window.clearInterval(timer);
        return;
      }

      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      const settled = lastTop !== null && Math.abs(top - lastTop) < 2;
      lastTop = top;
      if (!settled && !giveUp) return;

      scrolledToMap.current = true;
      window.clearInterval(timer);
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }, 250);

    return () => window.clearInterval(timer);
  }, []);

  /**
   * Date filters (Date objects, for the DateRangeDropdown).
   *
   * Default to year to date, matching the Overview and LPI pages. The page used
   * to open with no period at all, so it showed nothing until the user chose a
   * range — and the figure it then showed could not be compared with the
   * dashboard they had just come from, which was already on YTD.
   *
   * A URL parameter still wins, so shared and drilled-into links keep their own
   * period instead of being overwritten by the default.
   */
  const [startDate, setStartDate] = useState<Date | null>(
    () => parseDate(searchParams.get('start_date') || '') ?? parseDate(ytdRange().start)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    () => parseDate(searchParams.get('end_date') || '') ?? parseDate(ytdRange().end)
  );

  // Client filter state
  const [clients, setClients] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>(() => {
    const clientIds = searchParams.get('clientId');
    return clientIds ? clientIds.split(',').filter(Boolean) : [];
  });
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);
  const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(() => {
    const getArrayParam = (key: string) => {
      const val = searchParams.get(key);
      return val ? val.split(',').filter(Boolean) : [];
    };
    return {
      carrierIds: getArrayParam('carrierId'),
      fuelTypes: getArrayParam('fuelType'),
      modalities: getArrayParam('modality'),
      emissionSources: getArrayParam('emissionSource'),
      shipperIds: getArrayParam('shipperId'),
      tocs: getArrayParam('tocs'),
    };
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showFilterInfo, setShowFilterInfo] = useState(false);
  // TOC key -> display-label map, reported up by AdvancedFilterDrawer once
  // fetched, so FilterPills can show TOC pills by label while still removing
  // by the canonical key underneath.
  const [tocLabels, setTocLabels] = useState<Record<string, string>>({});
  const handleTocsLoaded = useCallback((tocs: Array<{ key: string; label: string }>) => {
    setTocLabels(Object.fromEntries(tocs.map((o) => [o.key, o.label])));
  }, []);

  // react-query client used by the orchestrator (queryClient.fetchQuery) and
  // by the 7 CO2e useQuery hooks below.
  const queryClient = useQueryClient();

  // Canonical params object used as the variable part of every CPI cache key.
  // Recomputed only when an input actually changes — useful for stable refs
  // even though react-query uses structural compare on cache keys.
  const cpiParams: CpiQueryParams | null = useMemo(() => {
    if (!startDate || !endDate) return null;
    return buildCpiQueryParams(startDate, endDate, selectedClients, advancedFilters);
  }, [startDate, endDate, selectedClients, advancedFilters]);

  /**
   * Region map data. Only fetched while the Emissions report view is open.
   *
   * The unhonourable filters (fuelType, emissionSource, shipperId, tocs) are passed
   * through on purpose: the endpoint names them back in `ignoredFilters` so the map
   * can tell the user it is less filtered than the charts beside it.
   */
  const regionQuery = useRegionEmissions(
    {
      startDate: cpiParams?.startDate ?? '',
      endDate: cpiParams?.endDate ?? '',
      clientId: nonEmpty(cpiParams?.clientIds),
      carrierId: nonEmpty(cpiParams?.carrierIds),
      modality: nonEmpty(cpiParams?.modalities),
      fuelType: nonEmpty(cpiParams?.fuelTypes),
      emissionSource: nonEmpty(cpiParams?.emissionSources),
      shipperId: nonEmpty(cpiParams?.shipperIds),
      tocs: nonEmpty(cpiParams?.tocs),
      // Finest stored resolution: the map rolls up locally as the user zooms and
      // cannot invent detail the server did not send.
      resolution: 4,
    },
    /**
     * Never self-fetches. Every other CPI query on this page is enabled:false so
     * the orchestrator can stage them: the summary alone first, then wave 1 at
     * concurrency 2, then wave 2. Five concurrent CPI queries on a year of data
     * have OOM'd the data-processor, which is what that budget exists to avoid.
     *
     * Left to itself this hook fetched the moment its parameters existed, making
     * peak concurrency 3 where the orchestrator deliberately holds it at 2. It is
     * a cheap read of a pre-aggregated table (~110-200ms measured), but it does
     * not get to opt out of the budget.
     */
    false
  );

  // Trips params — extends cpiParams with current pagination state.
  // Each page is a separate cache entry, so page + itemsPerPage are part of the key.
  const tripsParams: CpiTripsPageParams | null = useMemo(() => {
    if (!cpiParams) return null;
    return { ...cpiParams, page: tripsCurrentPage, itemsPerPage: tripsItemsPerPage };
  }, [cpiParams, tripsCurrentPage, tripsItemsPerPage]);

  // ---------------------------------------------------------------------------
  // Trips — react-query cache reader. enabled:false; handlers below use
  // queryClient.fetchQuery to populate the cache imperatively.
  // ---------------------------------------------------------------------------
  const tripsQuery = useQuery({
    queryKey: tripsParams ? tripsQueryKey(tripsParams) : (['cpi', 'trips', null] as const),
    queryFn: async ({ signal }) => {
      if (!tripsParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return tripsImpl(tripsParams, auth, signal);
    },
    enabled: false,
  });
  const tripsData = tripsQuery.data?.trips ?? [];
  const tripsTotalCount = tripsQuery.data?.totalCount ?? 0;
  const isLoadingTrips = tripsQuery.isFetching;
  const tripsLoaded = tripsQuery.data !== undefined;

  // ---------------------------------------------------------------------------
  // Dashboard summary — react-query cache reader. enabled:false means it never
  // auto-fetches on mount; the orchestrator (handleCalculateCPI below) populates
  // the cache via queryClient.fetchQuery and this subscription re-renders.
  // The alias keeps existing `dashboardData` JSX references working unchanged.
  // ---------------------------------------------------------------------------
  const dashboardQuery = useQuery({
    queryKey: cpiParams ? dashboardQueryKey(cpiParams) : (['cpi', 'dashboard', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return dashboardImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });
  const dashboardData = dashboardQuery.data ?? null;

  // ---------------------------------------------------------------------------
  // Emissions sub-tab charts — react-query cache readers. enabled:false means these
  // hooks never auto-fetch on mount; the orchestrator below populates the cache
  // via queryClient.fetchQuery and these subscribe reactively. The queryFn is
  // only invoked on explicit refetch (e.g. retry button on the chart). The wave
  // signal is passed to the orchestrator's fetchQuery, not these hooks.
  // ---------------------------------------------------------------------------
  const emissionsByCustomerQuery = useQuery({
    queryKey: cpiParams ? emissionsByCustomerQueryKey(cpiParams) : (['cpi', 'emissions-by-customer', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return emissionsByCustomerImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const emissionsByTocQuery = useQuery({
    queryKey: cpiParams ? emissionsByTocQueryKey(cpiParams) : (['cpi', 'emissions-by-toc', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return emissionsByTocImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const emissionsByMonthQuery = useQuery({
    queryKey: cpiParams ? emissionsByMonthQueryKey(cpiParams) : (['cpi', 'emissions-by-month', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return emissionsByMonthImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const intensityPerTonQuery = useQuery({
    queryKey: cpiParams ? intensityPerTonQueryKey(cpiParams) : (['cpi', 'intensity-per-ton-monthly', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return intensityPerTonImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const intensityPerTonKmQuery = useQuery({
    queryKey: cpiParams ? intensityPerTonKmQueryKey(cpiParams) : (['cpi', 'intensity-per-tonkm-monthly', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return intensityPerTonKmImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const intensityByModalityQuery = useQuery({
    queryKey: cpiParams ? intensityByModalityQueryKey(cpiParams) : (['cpi', 'intensity-per-ton-by-modality', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return intensityByModalityImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const intensityByVehicleQuery = useQuery({
    queryKey: cpiParams ? intensityByVehicleQueryKey(cpiParams) : (['cpi', 'intensity-per-tonkm-by-vehicle', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return intensityByVehicleImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const intensityByOrderQuery = useQuery({
    queryKey: cpiParams ? intensityByOrderQueryKey(cpiParams) : (['cpi', 'intensity-per-tonkm-by-order', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return intensityByOrderImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  // ---------------------------------------------------------------------------
  // Operational sub-tab charts — react-query cache readers. Same pattern as
  // the CO2e hooks above. enabled:false means no auto-fetch on mount; the
  // orchestrator populates the cache via queryClient.fetchQuery below.
  // ---------------------------------------------------------------------------
  const errorDistributionQuery = useQuery({
    queryKey: cpiParams ? errorDistributionQueryKey(cpiParams) : (['cpi', 'error-distribution', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return errorDistributionImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const shipmentsByCustomerQuery = useQuery({
    queryKey: cpiParams ? shipmentsByCustomerQueryKey(cpiParams) : (['cpi', 'shipments-by-customer', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return shipmentsByCustomerImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const weightByModalityQuery = useQuery({
    queryKey: cpiParams ? weightByModalityQueryKey(cpiParams) : (['cpi', 'weight-by-modality', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return weightByModalityImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const [dataQualitySortOrder, setDataQualitySortOrder] = useState<'asc' | 'desc'>('asc');

  const dataQualityByClientQuery = useQuery({
    queryKey: cpiParams
      ? dataQualityByClientQueryKey(cpiParams, dataQualitySortOrder)
      : (['cpi', 'data-quality-by-client', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return dataQualityByClientImpl(cpiParams, auth, signal, dataQualitySortOrder);
    },
    enabled: false,
  });

  // ---------------------------------------------------------------------------
  // Energy sub-tab charts — react-query cache readers. Same pattern as the Emissions
  // and Operational hooks above. enabled:false means no auto-fetch on mount;
  // the orchestrator populates the cache via queryClient.fetchQuery below.
  // ---------------------------------------------------------------------------
  const emissionSourceDistributionQuery = useQuery({
    queryKey: cpiParams ? emissionSourceDistributionQueryKey(cpiParams) : (['cpi', 'emission-source-distribution', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return emissionSourceDistributionImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const emissionsByFuelQuery = useQuery({
    queryKey: cpiParams ? emissionsByFuelQueryKey(cpiParams) : (['cpi', 'emissions-by-fuel', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return emissionsByFuelImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  const fuelConsumptionQuery = useQuery({
    queryKey: cpiParams ? fuelConsumptionQueryKey(cpiParams) : (['cpi', 'fuel-consumption', null] as const),
    queryFn: async ({ signal }) => {
      if (!cpiParams) return null;
      const auth: CpiAuth = await getSessionTokens(getSession);
      return fuelConsumptionImpl(cpiParams, auth, signal);
    },
    enabled: false,
  });

  // Adapt react-query state to the {data, isLoading, error, onRetry} props
  // the existing chart components already understand.
  const deriveChartProps = useCallback(<T,>(q: {
    data: T | undefined;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  }) => {
    let chartError: Error | null = null;
    if (q.isError) {
      chartError = q.error instanceof Error ? q.error : new Error(String(q.error));
    }
    return {
      data: q.data ?? null,
      isLoading: q.isFetching || (q.data === undefined && !q.isError),
      error: chartError,
      onRetry: () => { q.refetch(); },
    };
  }, []);

  // Shared AbortController for all CPI fetches on this page. Each "wave" of
  // requests (recalculate, sub-tab switch, trips load, clear filters) calls
  // startNewFetchWave() which aborts the previous in-flight requests before
  // creating a fresh controller. Without this, switching sub-tabs or changing
  // the date range mid-load leaks queries that race with the new wave —
  // peaking concurrent in-flight count above the data-processor memory budget
  // and silently overwriting newer state with older responses.
  const abortRef = useRef<AbortController | null>(null);
  const startNewFetchWave = useCallback((): AbortSignal => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller.signal;
  }, []);
  useEffect(() => () => abortRef.current?.abort(), []);

  // Dismiss filter info popover on outside click
  useEffect(() => {
    if (!showFilterInfo) return undefined;
    const dismiss = () => setShowFilterInfo(false);
    document.addEventListener('click', dismiss);
    return () => document.removeEventListener('click', dismiss);
  }, [showFilterInfo]);

  // Sync filters to URL
  const syncFiltersToURL = useCallback(() => {
    const params = new URLSearchParams();
    const startStr = formatDateToString(startDate);
    const endStr = formatDateToString(endDate);
    if (startStr) params.append('start_date', startStr);
    if (endStr) params.append('end_date', endStr);
    if (selectedClients.length > 0) params.append('clientId', selectedClients.join(','));
    if (advancedFilters.carrierIds.length > 0) params.append('carrierId', advancedFilters.carrierIds.join(','));
    if (advancedFilters.fuelTypes.length > 0) params.append('fuelType', advancedFilters.fuelTypes.join(','));
    if (advancedFilters.modalities.length > 0) params.append('modality', advancedFilters.modalities.join(','));
    if (advancedFilters.emissionSources.length > 0) params.append('emissionSource', advancedFilters.emissionSources.join(','));
    if (advancedFilters.shipperIds.length > 0) params.append('shipperId', advancedFilters.shipperIds.join(','));
    if (advancedFilters.tocs.length > 0) params.append('tocs', advancedFilters.tocs.join(','));
    if (emissionsView === 'list') params.append('view', emissionsView);
    setSearchParams(params);
  }, [startDate, endDate, selectedClients, advancedFilters, emissionsView, setSearchParams]);

  // Emissions view change handler
  const handleViewChange = useCallback(
    (view: EmissionsView) => {
      setEmissionsView(view);
      const params = new URLSearchParams(searchParams);
      if (view === 'list') {
        params.set('view', view);
      } else {
        params.delete('view');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Session tokens getter for child components
  const getSessionTokensFn = useCallback(
    () => getSessionTokens(getSession),
    [getSession]
  );

  // Reset clients loaded state when date range changes so they get refetched with new dates
  const resetClientsOnDateChange = useCallback(() => {
    setClientsLoaded(false);
    setClients([]);
  }, []);

  // Fetch clients list (lazy loaded on dropdown focus)
  const fetchClients = useCallback(async () => {
    if (clientsLoaded || isLoadingClients) return;
    if (!startDate || !endDate) return; // Require date range to fetch clients
    setIsLoadingClients(true);
    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const startDateStr = formatDateToString(startDate);
      const endDateStr = formatDateToString(endDate);

      const response = await axios.get<ClientListResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/lpi/clients/list`,
        {
          params: {
            startDate: startDateStr,
            endDate: endDateStr,
          },
          headers: {
            Authorization: `Session ${jwtToken}`,
            'X-Session-Id': sessionId,
          },
        }
      );

      if (response.status === 200 && response.data?.clients) {
        const fetchedClients = response.data.clients;
        setClients(fetchedClients);
        setClientsLoaded(true);
        // Filter out selected clients that are no longer in the list
        if (selectedClients.length > 0) {
          const validClients = selectedClients.filter((client) =>
            fetchedClients.includes(client)
          );
          if (validClients.length !== selectedClients.length) {
            setSelectedClients(validClients);
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch clients:', err);
    } finally {
      setIsLoadingClients(false);
    }
  }, [getSession, clientsLoaded, isLoadingClients, startDate, endDate, selectedClients]);

  // Reset clients when date range changes
  useEffect(() => {
    resetClientsOnDateChange();
  }, [startDate, endDate, resetClientsOnDateChange]);

  // Helper to add advanced filter params for URLSearchParams used by the export endpoints
  const appendAdvancedFilterSearchParams = useCallback((params: URLSearchParams) => {
    advancedFilters.carrierIds.forEach((id) => params.append('carrierId', id));
    advancedFilters.fuelTypes.forEach((fuel) => params.append('fuelType', fuel));
    advancedFilters.modalities.forEach((mod) => params.append('modality', mod));
    advancedFilters.emissionSources.forEach((src) => params.append('emissionSource', src));
    advancedFilters.shipperIds.forEach((id) => params.append('shipperId', id));
    advancedFilters.tocs.forEach((toc) => params.append('tocs', toc));
  }, [advancedFilters]);

  // fetchErrorDistribution removed — Operational sub-tab now uses the
  // errorDistributionQuery useQuery hook + queryClient.fetchQuery orchestrator.

  // Thin uncached wrapper used only by PPT export — bypasses the react-query
  // cache so a single PPT-only fetch doesn't pollute the dashboard cache.
  // The Energy sub-tab itself reads from `emissionSourceDistributionQuery` above.
  const fetchEmissionSourceDistribution = useCallback(async (): Promise<EmissionSourceDistributionResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return emissionSourceDistributionImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  // Thin uncached wrapper used only by PPT export — bypasses the react-query
  // cache so a single PPT-only fetch doesn't pollute the dashboard cache.
  // The Emissions sub-tab itself reads from `emissionsByMonthQuery` above.
  const fetchEmissionsByMonth = useCallback(async (): Promise<EmissionsByMonthResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return emissionsByMonthImpl(cpiParams, auth);
  }, [cpiParams, getSession]);


  // fetchWeightByModality and fetchShipmentsByCustomer removed — Operational
  // sub-tab now uses react-query hooks + queryClient.fetchQuery orchestrator.
  // Thin uncached wrapper retained for PPT export (cache-miss fallback only).
  const fetchWeightByModality = useCallback(async (): Promise<WeightByModalityResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return weightByModalityImpl(cpiParams, auth);
  }, [cpiParams, getSession]);


  // Intensity analytics — thin uncached wrappers used only by PPT export.
  // The Emissions sub-tab itself reads from the intensity*Query hooks above.
  const fetchIntensityPerTon = useCallback(async (): Promise<IntensityPerTonMonthlyResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return intensityPerTonImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  const fetchIntensityPerTonKm = useCallback(async (): Promise<IntensityPerTonKmMonthlyResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return intensityPerTonKmImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  const fetchIntensityByModality = useCallback(async (): Promise<IntensityByModalityResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return intensityByModalityImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  const fetchIntensityByVehicle = useCallback(async (): Promise<IntensityByVehicleResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return intensityByVehicleImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  const fetchIntensityByOrder = useCallback(async (): Promise<IntensityByOrderResponse | null> => {
    if (!cpiParams) return null;
    const auth = await getSessionTokens(getSession);
    return intensityByOrderImpl(cpiParams, auth);
  }, [cpiParams, getSession]);

  // Fetch one trips page into the cache. Takes the wave's signal rather than
  // starting its own, so the Apply orchestrator can load trips as the last step
  // of a wave without aborting the summary request it just made.
  const fetchTripsPage = useCallback(async (
    params: CpiQueryParams,
    page: number,
    perPage: number,
    signal: AbortSignal
  ) => {
    const nextParams: CpiTripsPageParams = { ...params, page, itemsPerPage: perPage };
    try {
      await queryClient.fetchQuery({
        queryKey: tripsQueryKey(nextParams),
        queryFn: async () => {
          const auth: CpiAuth = await getSessionTokens(getSession);
          return tripsImpl(nextParams, auth, signal);
        },
      });
    } catch (err: any) {
      // fetchQuery rejects on failure. Without this catch the rejection escapes
      // the callback — callers invoke it as a floating promise, so it surfaced
      // as an unhandled rejection instead of an error the user can read.
      if (signal.aborted || axios.isCancel(err)) return;
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('cpi.tripsError'));
      }
    }
  }, [queryClient, getSession, t]);

  // Handle trips page change
  const handleTripsPageChange = useCallback((page: number) => {
    if (!cpiParams) return;
    setTripsCurrentPage(page);
    fetchTripsPage(cpiParams, page, tripsItemsPerPage, startNewFetchWave());
  }, [cpiParams, tripsItemsPerPage, startNewFetchWave, fetchTripsPage]);

  // Handle trips items per page change
  const handleTripsItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    if (!cpiParams) return;
    setTripsItemsPerPage(newItemsPerPage);
    setTripsCurrentPage(1);
    fetchTripsPage(cpiParams, 1, newItemsPerPage, startNewFetchWave());
  }, [cpiParams, startNewFetchWave, fetchTripsPage]);

  // Export trips to Excel via server-side endpoint
  const handleExportToExcel = useCallback(async () => {
    if (!startDate || !endDate) return;

    setIsExporting(true);

    const startDateStr = formatDateToString(startDate);
    const endDateStr = formatDateToString(endDate);

    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const headers = {
        Authorization: `Session ${jwtToken}`,
        'X-Session-Id': sessionId,
      };

      // Preflight check: verify Excel export is supported for this dataset size
      const countParams = new URLSearchParams({ startDate: startDateStr, endDate: endDateStr });
      selectedClients.forEach((id) => countParams.append('clientId', id));
      appendAdvancedFilterSearchParams(countParams);

      const countResponse = await fetch(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/trips/export/count?${countParams}`,
        { headers }
      );

      if (countResponse.ok) {
        const countData = await countResponse.json();
        if (!countData.excelSupported) {
          // eslint-disable-next-line no-alert
          window.alert(countData.warning || t('cpi.excelNotSupported'));
          setIsExporting(false);
          return;
        }
        if (countData.warning) {
          // eslint-disable-next-line no-alert
          const confirmed = window.confirm(countData.warning);
          if (!confirmed) {
            setIsExporting(false);
            return;
          }
        }
      }

      const params = new URLSearchParams({ startDate: startDateStr, endDate: endDateStr });
      selectedClients.forEach((id) => params.append('clientId', id));
      appendAdvancedFilterSearchParams(params);

      const response = await fetch(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/trips/export?${params}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CPI_Trips_${startDateStr}_to_${endDateStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to export trips:', err);
      // eslint-disable-next-line no-alert
      window.alert(t('cpi.exportError'));
    } finally {
      setIsExporting(false);
    }
  }, [startDate, endDate, selectedClients, appendAdvancedFilterSearchParams, getSession, t]);

  // Export trips to CSV via server-side endpoint
  const handleExportToCsv = useCallback(async () => {
    if (!startDate || !endDate) return;

    setIsExportingCsv(true);

    const startDateStr = formatDateToString(startDate);
    const endDateStr = formatDateToString(endDate);

    try {
      const { jwtToken, sessionId } = await getSessionTokens(getSession);

      const headers = {
        Authorization: `Session ${jwtToken}`,
        'X-Session-Id': sessionId,
      };

      // Check export count and warn user if large dataset
      const countParams = new URLSearchParams({ startDate: startDateStr, endDate: endDateStr });
      selectedClients.forEach((id) => countParams.append('clientId', id));
      appendAdvancedFilterSearchParams(countParams);

      const countResponse = await fetch(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/trips/export/count?${countParams}`,
        { headers }
      );

      if (countResponse.ok) {
        const countData = await countResponse.json();
        if (countData.warning) {
          // eslint-disable-next-line no-alert
          const confirmed = window.confirm(countData.warning);
          if (!confirmed) {
            setIsExportingCsv(false);
            return;
          }
        }
      }

      const params = new URLSearchParams({ startDate: startDateStr, endDate: endDateStr, format: 'csv' });
      selectedClients.forEach((id) => params.append('clientId', id));
      appendAdvancedFilterSearchParams(params);

      const response = await fetch(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/trips/export?${params}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CPI_Trips_${startDateStr}_to_${endDateStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to export trips to CSV:', err);
      // eslint-disable-next-line no-alert
      window.alert(t('cpi.exportError'));
    } finally {
      setIsExportingCsv(false);
    }
  }, [startDate, endDate, selectedClients, appendAdvancedFilterSearchParams, getSession, t]);

  // Export dashboard to PowerPoint
  const handleExportToPpt = useCallback(async () => {
    if (!startDate || !endDate || !dashboardData) return;

    setIsExportingPpt(true);

    try {
      // Lazy-fetch any data not already loaded by the active sub-tab.
      // Use captured locals because React state from this closure is stale
      // until the next render, even after setState inside the fetcher.
      //
      // For the 6 CO2e charts, prefer the react-query cache (populated by the
      // Emissions sub-tab orchestrator). If the sub-tab has been opened in this
      // session, the cache entry exists; otherwise fall back to a lazy fetch
      // via the uncached wrapper fetchers.
      let resolvedEmissionsByMonth: EmissionsByMonthResponse | null =
        cpiParams ? (queryClient.getQueryData<EmissionsByMonthResponse | null>(emissionsByMonthQueryKey(cpiParams)) ?? null) : null;
      let resolvedIntensityPerTon: IntensityPerTonMonthlyResponse | null =
        cpiParams ? (queryClient.getQueryData<IntensityPerTonMonthlyResponse | null>(intensityPerTonQueryKey(cpiParams)) ?? null) : null;
      let resolvedIntensityPerTonKm: IntensityPerTonKmMonthlyResponse | null =
        cpiParams ? (queryClient.getQueryData<IntensityPerTonKmMonthlyResponse | null>(intensityPerTonKmQueryKey(cpiParams)) ?? null) : null;
      let resolvedIntensityByModality: IntensityByModalityResponse | null =
        cpiParams ? (queryClient.getQueryData<IntensityByModalityResponse | null>(intensityByModalityQueryKey(cpiParams)) ?? null) : null;
      let resolvedIntensityByVehicle: IntensityByVehicleResponse | null =
        cpiParams ? (queryClient.getQueryData<IntensityByVehicleResponse | null>(intensityByVehicleQueryKey(cpiParams)) ?? null) : null;
      let resolvedIntensityByOrder: IntensityByOrderResponse | null =
        cpiParams ? (queryClient.getQueryData<IntensityByOrderResponse | null>(intensityByOrderQueryKey(cpiParams)) ?? null) : null;
      let resolvedWeightByModality: WeightByModalityResponse | null =
        cpiParams ? (queryClient.getQueryData<WeightByModalityResponse | null>(weightByModalityQueryKey(cpiParams)) ?? null) : null;
      let resolvedEmissionSource: EmissionSourceDistributionResponse | null =
        cpiParams ? (queryClient.getQueryData<EmissionSourceDistributionResponse | null>(emissionSourceDistributionQueryKey(cpiParams)) ?? null) : null;

      const tasks: Array<() => Promise<unknown>> = [];
      if (!resolvedEmissionsByMonth) tasks.push(async () => { resolvedEmissionsByMonth = await fetchEmissionsByMonth(); });
      if (!resolvedIntensityPerTon) tasks.push(async () => { resolvedIntensityPerTon = await fetchIntensityPerTon(); });
      if (!resolvedIntensityPerTonKm) tasks.push(async () => { resolvedIntensityPerTonKm = await fetchIntensityPerTonKm(); });
      if (!resolvedIntensityByModality) tasks.push(async () => { resolvedIntensityByModality = await fetchIntensityByModality(); });
      if (!resolvedWeightByModality) tasks.push(async () => { resolvedWeightByModality = await fetchWeightByModality(); });
      if (!resolvedEmissionSource) tasks.push(async () => { resolvedEmissionSource = await fetchEmissionSourceDistribution(); });
      if (!resolvedIntensityByVehicle) tasks.push(async () => { resolvedIntensityByVehicle = await fetchIntensityByVehicle(); });
      if (!resolvedIntensityByOrder) tasks.push(async () => { resolvedIntensityByOrder = await fetchIntensityByOrder(); });

      if (tasks.length > 0) {
        // Concurrency limit 2 keeps backend memory bounded — the heaviest two
        // queries (intensity-by-vehicle, intensity-by-order) won't co-execute
        // with each other or with another heavy fetch.
        await runWithConcurrency(tasks, 2);
      }

      if (!resolvedEmissionsByMonth || !resolvedIntensityPerTon || !resolvedIntensityPerTonKm ||
          !resolvedIntensityByModality || !resolvedIntensityByVehicle || !resolvedIntensityByOrder ||
          !resolvedWeightByModality) {
        // eslint-disable-next-line no-alert
        window.alert(t('cpi.exportPptError'));
        return;
      }

      const formatDisplayDate = (d: Date) =>
        `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;

      const clientName = selectedClients.length === 1
        ? selectedClients[0]
        : 'Alle klanten';

      const startStr = formatDisplayDate(startDate);
      const endStr = formatDisplayDate(endDate);

      const exportData: CpiPptExportData = {
        cover: { clientName, startDate: startStr, endDate: endStr, logoUrl: userInfo.logoUrl || undefined },
        overview: {
          clientName, startDate: startStr, endDate: endStr,
          dashboard: dashboardData,
          emissionSource: resolvedEmissionSource,
        },
        emissions: {
          clientName, startDate: startStr, endDate: endStr,
          dashboard: dashboardData,
          emissionsByMonth: resolvedEmissionsByMonth,
        },
        co2ePeriod: {
          clientName, startDate: startStr, endDate: endStr,
          intensityPerTon: resolvedIntensityPerTon,
          intensityPerTonKm: resolvedIntensityPerTonKm,
        },
        co2eDetail: {
          clientName, startDate: startStr, endDate: endStr,
          intensityByOrder: resolvedIntensityByOrder,
          intensityByVehicle: resolvedIntensityByVehicle,
          intensityByModality: resolvedIntensityByModality,
        },
        operational: {
          clientName, startDate: startStr, endDate: endStr,
          weightByModality: resolvedWeightByModality,
        },
      };

      await exportCpiToPpt(exportData);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to export PPT:', err);
      // eslint-disable-next-line no-alert
      window.alert(t('cpi.exportPptError'));
    } finally {
      setIsExportingPpt(false);
    }
  }, [startDate, endDate, selectedClients, dashboardData, cpiParams, queryClient,
      userInfo.logoUrl, t,
      fetchEmissionsByMonth, fetchIntensityPerTon, fetchIntensityPerTonKm, fetchIntensityByModality,
      fetchIntensityByVehicle, fetchIntensityByOrder, fetchWeightByModality, fetchEmissionSourceDistribution]);

  // Fetch charts for a specific sub-tab
  const fetchSubTabData = useCallback((subTab: DashboardSubTab, signal: AbortSignal) => {
    switch (subTab) {
      case 'emissions': {
        // CO2e charts are served from the react-query cache. Each call to
        // queryClient.fetchQuery resolves immediately from cache on a sub-tab
        // bounce (staleTime: Infinity) or fetches and stores otherwise — the
        // useQuery hooks above subscribe and re-render automatically.
        //
        // The wave signal still gates the HTTP calls so a sub-tab switch or
        // date-range change mid-load aborts the in-flight axios requests
        // exactly as before, and the .then() chain below skips wave 2.
        if (!cpiParams) break;
        const fetchOne = <T,>(
          queryKey: readonly unknown[],
          impl: (p: CpiQueryParams, auth: CpiAuth, s?: AbortSignal) => Promise<T | null>
        ) =>
          queryClient.fetchQuery({
            queryKey: queryKey as readonly unknown[],
            queryFn: async () => {
              const auth = await getSessionTokens(getSession);
              return impl(cpiParams, auth, signal);
            },
          });
        // Wave 1: 6 lighter-to-medium queries throttled at concurrency 2.
        // Empirically, 5 concurrent CPI queries on a year of data peak around
        // 4 GiB Prisma engine memory and OOM the data-processor; 2 concurrent
        // peaks around 1.5 GiB and fits comfortably under the cgroup cap.
        //
        // Queue order is fast-first, not visual top-to-bottom: emissions-by-*
        // with compareWithPreviousYear=true take ~27s on arbitrary mid-year
        // ranges (the comparison leg scans the previous year's raw data
        // without a usable index/rollup). If they sit at the head of the
        // queue they hold both worker slots and nothing on the page paints
        // for ~27s. Running the three intensity charts first lets the middle
        // of the page populate within ~3s while the compare-year queries
        // finish in parallel. emissions-by-toc is grouped by a single simple
        // category (like emissions-by-customer above), not a compare-year
        // query, so it rides along in this wave rather than wave 2.
        const wave1 = runWithConcurrency<unknown>([
          () => fetchOne(intensityPerTonQueryKey(cpiParams), intensityPerTonImpl),
          () => fetchOne(intensityPerTonKmQueryKey(cpiParams), intensityPerTonKmImpl),
          () => fetchOne(intensityByModalityQueryKey(cpiParams), intensityByModalityImpl),
          () => fetchOne(emissionsByCustomerQueryKey(cpiParams), emissionsByCustomerImpl),
          () => fetchOne(emissionsByMonthQueryKey(cpiParams), emissionsByMonthImpl),
          () => fetchOne(emissionsByTocQueryKey(cpiParams), emissionsByTocImpl),
        ], 2);
        // Wave 2: the two heaviest raw fact_cpi_calculations GROUP BY queries.
        // Run together (peak 2 concurrent) only after wave 1 has fully released
        // its memory. Skip entirely if the wave was aborted — otherwise a
        // sub-tab switch mid-load would fire these two heavy queries even
        // though their results would never be rendered.
        // Errors are already surfaced via react-query's per-query isError state
        // via deriveChartProps; the orchestrator just needs to swallow the
        // rejection to avoid unhandled-promise noise.
        wave1.then(() => {
          if (signal.aborted) return;
          fetchOne(intensityByVehicleQueryKey(cpiParams), intensityByVehicleImpl).catch(() => {});
          fetchOne(intensityByOrderQueryKey(cpiParams), intensityByOrderImpl).catch(() => {});
          // The map, last. It is the cheapest query on the tab — a read of a
          // pre-aggregated summary — but it is still a query, and nothing here
          // sits outside the staging that exists to keep peak concurrency at 2.
          regionQuery.refetch().catch(() => {});
        }).catch(() => {});
        break;
      }
      case 'operational': {
        if (!cpiParams) break;
        const fetchOne = <T,>(
          queryKey: readonly unknown[],
          impl: (p: CpiQueryParams, auth: CpiAuth, s?: AbortSignal) => Promise<T | null>
        ) =>
          queryClient.fetchQuery({
            queryKey: queryKey as readonly unknown[],
            queryFn: async () => {
              const auth = await getSessionTokens(getSession);
              return impl(cpiParams, auth, signal);
            },
          });
        fetchOne(errorDistributionQueryKey(cpiParams), errorDistributionImpl).catch(() => {});
        fetchOne(shipmentsByCustomerQueryKey(cpiParams), shipmentsByCustomerImpl).catch(() => {});
        fetchOne(weightByModalityQueryKey(cpiParams), weightByModalityImpl).catch(() => {});
        queryClient.fetchQuery({
          queryKey: dataQualityByClientQueryKey(cpiParams, dataQualitySortOrder),
          queryFn: async ({ signal: fetchSignal }) => {
            const auth: CpiAuth = await getSessionTokens(getSession);
            return dataQualityByClientImpl(cpiParams, auth, fetchSignal, dataQualitySortOrder);
          },
        }).catch(() => {});
        break;
      }
      case 'energy': {
        if (!cpiParams) break;
        const fetchOne = <T,>(
          queryKey: readonly unknown[],
          impl: (p: CpiQueryParams, auth: CpiAuth, s?: AbortSignal) => Promise<T | null>
        ) =>
          queryClient.fetchQuery({
            queryKey: queryKey as readonly unknown[],
            queryFn: async () => {
              const auth = await getSessionTokens(getSession);
              return impl(cpiParams, auth, signal);
            },
          });
        fetchOne(emissionSourceDistributionQueryKey(cpiParams), emissionSourceDistributionImpl).catch(() => {});
        fetchOne(emissionsByFuelQueryKey(cpiParams), emissionsByFuelImpl).catch(() => {});
        fetchOne(fuelConsumptionQueryKey(cpiParams), fuelConsumptionImpl).catch(() => {});
        break;
      }
      default:
        break;
    }
    // regionQuery.refetch, not regionQuery: the object is new every render and
    // would rebuild this callback — and the effect that depends on it — on every
    // one. The bound refetch changes only when the query key does, which is
    // exactly when a stale closure would otherwise refetch the previous range.
  }, [cpiParams, queryClient, getSession, dataQualitySortOrder, regionQuery.refetch]);

  // Handle sub-tab change with lazy loading
  const handleSubTabChange = useCallback((subTab: DashboardSubTab) => {
    setDashboardSubTab(subTab);
    if (!startDate || !endDate) return;
    // Abort any in-flight requests from the previous sub-tab (or recalc)
    // before kicking off the new wave. Without this, the previous wave's
    // responses race with the new wave and silently overwrite state.
    const signal = startNewFetchWave();
    fetchSubTabData(subTab, signal);
  }, [startDate, endDate, fetchSubTabData, startNewFetchWave]);

  // Load the dashboard summary and then whatever the open sub-tab shows.
  //
  // `refresh` separates the two callers. Apply passes true: it invalidates
  // every CPI query so the fetches below treat the cache as stale and refetch,
  // because otherwise clicking Apply with unchanged filters would be a silent
  // no-op cache hit (staleTime: Infinity on the QueryClient) and the user would
  // see no network activity at all. Entering a sub-tab view passes false, so an
  // already-cached summary or page is reused instead of re-requested.
  /**
   * True from the first request of a wave to the last, which `isLoading` is not.
   *
   * `isLoading` is cleared as soon as the summary lands, so the headline numbers
   * paint while the rest streams in. That leaves a gap: in React 17 a setState
   * inside a promise continuation flushes synchronously, so the moment the
   * summary resolved the auto-load effects re-ran and saw isLoading false,
   * isLoadingTrips false and tripsLoaded false — all true, because the trips
   * request had not started yet. They fired a second wave, whose
   * startNewFetchWave aborted the signal the first wave was one line away from
   * fetching trips with. The request showed as cancelled; Apply looked fine
   * because it calls loadCpiData directly rather than through those guards.
   */
  const waveInFlight = useRef(false);

  const loadCpiData = useCallback(async (refresh: boolean) => {
    // Require date range + canonical params
    if (!startDate || !endDate || !cpiParams) {
      setError(t('cpi.selectDateRange'));
      return;
    }

    // Abort any in-flight CPI fetches from a previous recalc / sub-tab switch
    // before starting this wave. Without this, slower responses from a wider
    // prior date range can land AFTER the new wave's responses and silently
    // overwrite state with stale data.
    const signal = startNewFetchWave();
    waveInFlight.current = true;

    // Set before the await, not after: invalidating clears the trips cache, and
    // the list view's auto-load effect watches for exactly that. It skips while
    // isLoading is true, so flipping the flag first is what stops the effect
    // from firing in the gap and aborting the summary request below.
    setIsLoading(true);
    setError('');
    setShowNoDataMessage(false);

    if (refresh) {
      await queryClient.invalidateQueries({ queryKey: ['cpi'] });
      if (signal.aborted) return;
    }

    // Reset trips pagination; data/loaded derive from the cache, not from state
    setTripsCurrentPage(1);
    // Reset chart states — all sub-tabs now use react-query; the new cpiParams
    // produces fresh query keys so the prior range's cached data is no longer
    // the active subscription, and the charts naturally re-render as empty.

    // Fetch the dashboard summary FIRST so the user sees the headline numbers
    // populate before sub-tab charts start streaming in. Awaiting also drops
    // the peak concurrent in-flight count from 3 (dashboard + 2 wave-1 workers)
    // to 2 (just the workers), giving the data-processor more headroom.
    let dashboardOk = true;
    try {
      const auth: CpiAuth = await getSessionTokens(getSession);
      if (signal.aborted) return;

      const dashboard = await queryClient.fetchQuery({
        queryKey: dashboardQueryKey(cpiParams),
        queryFn: async () => dashboardImpl(cpiParams, auth, signal),
      });
      if (signal.aborted) return;

      if (dashboard && dashboard.businessOrderCount > 0) {
        setShowNoDataMessage(false);
      } else {
        setShowNoDataMessage(true);
        dashboardOk = false;
      }
    } catch (err: any) {
      if (signal.aborted || axios.isCancel(err)) return;
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('cpi.calculationError'));
      }
      setShowNoDataMessage(false);
      dashboardOk = false;
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }

    // Only fetch the sub-tab's contents if the dashboard returned data —
    // there's nothing to chart or tabulate for empty / errored ranges.
    try {
      if (!signal.aborted && dashboardOk) {
        if (isTripsListView) {
          // Trip Details shows trips, not charts. Loading page 1 inside this same
          // wave is what keeps the table in step with the summary above it — the
          // list view used to skip the summary entirely, which left the whole
          // panel unmounted whenever the date range changed.
          await fetchTripsPage(cpiParams, 1, tripsItemsPerPage, signal);
        } else {
          fetchSubTabData(dashboardSubTab, signal);
        }
      }
    } finally {
      // Lowered only here. Clearing it with isLoading would reopen the very gap
      // this exists to close. A later wave supersedes this one by aborting its
      // signal, so an abandoned wave cannot leave the flag raised.
      waveInFlight.current = false;
    }
  }, [startDate, endDate, cpiParams, getSession, t, dashboardSubTab, fetchSubTabData,
    startNewFetchWave, queryClient, isTripsListView, fetchTripsPage, tripsItemsPerPage]);

  const handleCalculateCPI = useCallback(() => loadCpiData(true), [loadCpiData]);

  // Auto-load Trip Details whenever the list view is open and has no data for
  // the current filters — on entering the view, and on a date-range change,
  // which re-keys every CPI query and so empties both the summary and the
  // trips. Routing through loadCpiData(false) loads the summary too, so the
  // KPI cards above the table never sit blank; a cached summary costs nothing.
  //
  // `tripsQuery.isError` is part of the guard on purpose. A failed load leaves
  // `data` undefined, so `tripsLoaded` stays false while `isFetching` returns to
  // false — without the error check those two conditions are true again the
  // moment the request fails, and the effect re-fires forever. Changing filters
  // builds a new query key (clearing the error), and Apply calls loadCpiData
  // directly, so a deliberate retry still works. `showNoDataMessage` is in the
  // guard for the same reason: an empty range never populates the trips cache.
  useEffect(() => {
    if (
      isTripsListView &&
      startDate &&
      endDate &&
      !waveInFlight.current &&
      !tripsLoaded &&
      !isLoadingTrips &&
      !isLoading &&
      !showNoDataMessage &&
      !tripsQuery.isError
    ) {
      loadCpiData(false);
    }
  }, [isTripsListView, startDate, endDate, tripsLoaded, isLoadingTrips, isLoading,
    showNoDataMessage, tripsQuery.isError, loadCpiData]);

  /**
   * Load the dashboard on arrival, the way the Overview and LPI pages do.
   *
   * The page opened showing an introduction and a Calculate button: no charts,
   * no map, nothing to deep-link to. Now that the period defaults to year to
   * date there is always something to ask for, so asking for it is what the
   * user expects — pressing Calculate to see the page you just opened is not a
   * decision, it is a step.
   *
   * Guarded like the trips effect beside it, and for the same reasons: without
   * `dashboardQuery.isError` a failed load leaves `data` undefined while
   * `isFetching` returns to false, so both conditions are true again the instant
   * the request fails and the effect re-fires forever. Changing filters re-keys
   * the query and clears the error, and Apply calls loadCpiData directly, so a
   * deliberate retry still works.
   */
  useEffect(() => {
    if (
      !isTripsListView &&
      startDate &&
      endDate &&
      !waveInFlight.current &&
      dashboardQuery.data === undefined &&
      !dashboardQuery.isFetching &&
      !isLoading &&
      !showNoDataMessage &&
      !dashboardQuery.isError
    ) {
      loadCpiData(false);
    }
  }, [isTripsListView, startDate, endDate, dashboardQuery.data, dashboardQuery.isFetching,
    dashboardQuery.isError, isLoading, showNoDataMessage, loadCpiData]);

  // Handle apply filters
  const handleApplyFilters = () => {
    syncFiltersToURL();
    handleCalculateCPI();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    // Abort any in-flight requests; otherwise their responses will land after
    // this clear and repopulate the state we just emptied.
    abortRef.current?.abort();
    setStartDate(null);
    setEndDate(null);
    setSelectedClients([]);
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
    const params = new URLSearchParams();
    if (emissionsView === 'list') params.set('view', emissionsView);
    setSearchParams(params);
    // All cache entries (including dashboard) are abandoned: with
    // startDate=endDate=null the useQuery hooks subscribe to the sentinel
    // [..., null] key which has no cached data, so the dashboard summary
    // and charts naturally re-render as empty. trips data/loaded derive
    // from the cache; only pagination state needs explicit reset.
    setError('');
    setShowNoDataMessage(false);
    setTripsCurrentPage(1);
  };

  const drawerApplyPending = useRef(false);

  const handleDrawerApply = useCallback((filters: AdvancedFilters) => {
    setAdvancedFilters(filters);
    drawerApplyPending.current = true;
  }, []);

  // Auto-fetch data after drawer Apply updates filter state
  useEffect(() => {
    if (!drawerApplyPending.current) return;
    drawerApplyPending.current = false;
    syncFiltersToURL();
    handleCalculateCPI();
  }, [advancedFilters, syncFiltersToURL, handleCalculateCPI]);

  const handleDrawerReset = useCallback(() => {
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
  }, []);

  const handleRemovePill = useCallback((filterKey: keyof AdvancedFilters, value: string) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey].filter((v) => v !== value),
    }));
  }, []);

  const handleClearAllPills = useCallback(() => {
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
  }, []);

  // Trip Details renders on its own terms: its content is the trips table, so
  // an open list view with a date range is enough to show the panel. Gating it
  // on `dashboardData` alone meant a date-range change — which re-keys the
  // summary query and empties it — unmounted the table mid-session and dropped
  // the page back to the "get started" prompt.
  const showDashboardPanel =
    (Boolean(dashboardData) || isLoading || (isTripsListView && Boolean(startDate && endDate))) &&
    !showNoDataMessage;

  const activeFilterCount = advancedFilters.carrierIds.length +
    advancedFilters.fuelTypes.length +
    advancedFilters.modalities.length +
    advancedFilters.emissionSources.length +
    advancedFilters.shipperIds.length +
    advancedFilters.tocs.length;

  return (
    <div className="cpi-reports container">
      {/* The visible page title is gone — the nav item already names this page.
          The heading stays in the markup for screen readers, where heading
          navigation is how people establish where they are. */}
      <h1 className="cpi-reports__sr-only">{t('CPI Reports')}</h1>

      {/* Zero-height marker the stuck-detection hook watches. It has to sit
          outside the bar: once the bar pins, it stops moving, so only something
          above it can tell us we have scrolled past. */}
      <div ref={sentinelRef} className="cpi-reports__filter-sentinel" aria-hidden="true" />

      {/* Filter Bar */}
      <div className={`cpi-reports__filter-bar${isStuck ? ' cpi-reports__filter-bar--stuck' : ''}`}>
        <div className="cpi-reports__filter-group">
          <DateRangeDropdown
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            isLoading={isLoading}
          />

          {/* Client Filter */}
          <MultiSelectDropdown
            label={t('cpi.client')}
            options={clients}
            selected={selectedClients}
            onChange={setSelectedClients}
            placeholder={t('cpi.allClients')}
            isLoading={isLoading || isLoadingClients}
            onOpen={fetchClients}
            showSelectAll={false}
            minWidth="250px"
          />

          {/* Advanced Filters Button — wrapped to align with label+input dropdowns */}
          <div
            className={`cpi-reports__advanced-filter-wrap${!startDate || !endDate ? ' cpi-reports__advanced-filter-wrap--disabled' : ''}`}
          >
            <span className="cpi-reports__advanced-filter-spacer">&nbsp;</span>
            <div className="cpi-reports__advanced-filter-row">
              <button
                type="button"
                className="btn btn-outline-primary cpi-reports__advanced-filter-btn"
                onClick={() => setIsDrawerOpen(true)}
                disabled={isLoading || !startDate || !endDate}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 2h13l-5 6v4.5L7.5 14V8L1.5 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('cpi.advancedFilters')}
                {activeFilterCount > 0 && (
                  <span className="cpi-reports__filter-badge">{activeFilterCount}</span>
                )}
              </button>
              {(!startDate || !endDate) && (
                <div className="cpi-reports__filter-info-wrap">
                  <button
                    type="button"
                    className="cpi-reports__filter-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowFilterInfo((prev) => !prev); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="8" cy="4.5" r="0.75" fill="currentColor"/>
                    </svg>
                  </button>
                  {showFilterInfo && (
                    <div className="cpi-reports__filter-info-popover">
                      {t('cpi.advancedFilters.selectDateRange')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="cpi-reports__filter-actions">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            {t('cpi.clearFilters')}
          </button>
          <button
            type="button"
            className="btn btn-primary cpi-reports__calculate-btn"
            onClick={handleApplyFilters}
            disabled={isLoading || !startDate || !endDate}
          >
            {isLoading && (
              <>
                <span className="spinner spinner-sm" />
                {t('cpi.calculating')}
              </>
            )}
            {!isLoading && (
              <>
                {/* recalculating over a new period is the common case, so the
                    icon reads as "run this again" rather than "start" */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <polyline points="21 3 21 9 15 9" />
                </svg>
                {t('cpi.calculate')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filter Pills */}
      <FilterPills
        filters={advancedFilters}
        onRemove={handleRemovePill}
        onClearAll={handleClearAllPills}
        tocLabels={tocLabels}
      />

      {/* Advanced Filter Drawer */}
      <AdvancedFilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onApply={handleDrawerApply}
        onReset={handleDrawerReset}
        filters={advancedFilters}
        getSessionTokens={getSessionTokensFn}
        onTocsLoaded={handleTocsLoaded}
      />


      {/* Error Display — page level, not inside the dashboard panel. Trips
          failures set this too, and the dashboard panel is `hidden` while the
          list view is open, so an alert nested in the charts would be invisible
          exactly when the trips load is the thing that failed. */}
      {error && (
        <div
          role="alert"
          className="alert alert-danger alert-sm alert-dismissible"
          data-testid="cpi-error"
        >
          {error}
        </div>
      )}

      {/* Dashboard Tab Content */}
      <div className="cpi-reports__tab-content">
        {/* No Data Message */}
        {showNoDataMessage && !isLoading && (
          <div className="cpi-reports__no-data-section">
            <div className="cpi-reports__no-data-message">
              <span className="cpi-reports__no-data-icon">📊</span>
              <h3>{t('cpi.noDataAvailable')}</h3>
              <p>{t('cpi.noDataMessage')}</p>
            </div>
          </div>
        )}

        {/* Dashboard Content — suppressed when the period returned no orders.
            The API still hands back a dashboard object full of zeros in that
            case, so without this check the page shows "No Data Available" and
            a row of 0s at the same time. */}
        {showDashboardPanel && (
          <div className="cpi-reports__dashboard">
            {/* KPI Summary Cards */}
            <CpiSummaryCards data={dashboardData} isLoading={isLoading} />

            {/* Dashboard Sub-Tabs */}
            <div className="cpi-reports__sub-tab-bar">
              <button
                type="button"
                className={`cpi-reports__sub-tab ${dashboardSubTab === 'emissions' ? 'cpi-reports__sub-tab--active' : ''}`}
                onClick={() => handleSubTabChange('emissions')}
              >
                {t('cpi.subtab.emissions')}
              </button>
              <button
                type="button"
                className={`cpi-reports__sub-tab ${dashboardSubTab === 'operational' ? 'cpi-reports__sub-tab--active' : ''}`}
                onClick={() => handleSubTabChange('operational')}
              >
                {t('cpi.subtab.operational')}
              </button>
              <button
                type="button"
                className={`cpi-reports__sub-tab ${dashboardSubTab === 'energy' ? 'cpi-reports__sub-tab--active' : ''}`}
                onClick={() => handleSubTabChange('energy')}
              >
                {t('cpi.subtab.energy')}
              </button>
              {dashboardSubTab === 'emissions' && (
                <ViewSwitch
                  label={t('cpi.display')}
                  variant="icons"
                  value={emissionsView}
                  onChange={handleViewChange}
                  options={[
                    {
                      value: 'report',
                      label: t('cpi.view.report'),
                      panelId: 'cpi-view-report',
                      icon: (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M2 13.5h12M4 11V7m4 4V3m4 8V6" stroke="currentColor"
                            strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      ),
                    },
                    {
                      value: 'list',
                      label: t('cpi.view.list'),
                      panelId: 'cpi-view-list',
                      icon: (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor"
                            strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      ),
                    },
                  ]}
                />
              )}
              <button
                type="button"
                className="flat-action cpi-reports__client-report-btn"
                onClick={handleExportToPpt}
                disabled={isExportingPpt || !dashboardData}
              >
                {isExportingPpt ? (
                  <span className="spinner spinner-sm" />
                ) : (
                  /* a deck, not a spreadsheet — distinct from the download arrow
                     the trip exports use */
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="12" rx="2" />
                    <path d="M12 15v4" />
                    <path d="M8 21h8" />
                  </svg>
                )}
                {t('cpi.clientReport')}
              </button>
            </div>

            {/* Emissions Sub-Tab — list view holds what used to be the
                Trip Details page; report view holds the matrix and charts. */}
            {dashboardSubTab === 'emissions' && emissionsView === 'list' && (
              <div id="cpi-view-list" role="tabpanel" aria-labelledby="cpi-view-list-tab">
                <CpiTripsTable
                  trips={tripsData}
                  isLoading={isLoadingTrips}
                  totalCount={tripsTotalCount}
                  currentPage={tripsCurrentPage}
                  itemsPerPage={tripsItemsPerPage}
                  onPageChange={handleTripsPageChange}
                  onItemsPerPageChange={handleTripsItemsPerPageChange}
                  onExport={handleExportToExcel}
                  isExporting={isExporting}
                  onExportCsv={handleExportToCsv}
                  isExportingCsv={isExportingCsv}
                />
              </div>
            )}

            {dashboardSubTab === 'emissions' && emissionsView === 'report' && (
              <div id="cpi-view-report" role="tabpanel" aria-labelledby="cpi-view-report-tab">
                {/* Emissions Matrix Table */}
                <CpiEmissionsMatrix data={dashboardData} isLoading={isLoading} />

                {/* Anchored so "Open full map" on the Overview lands ON the map
                    rather than at the top of a long page of charts. */}
                <div className="cpi-reports__charts-grid" id={EMISSIONS_MAP_ANCHOR}>
                  {/* The region map renders a server response this page does not
                      otherwise validate end-to-end. A boundary here, rather than
                      around the whole page, means a future crash in this chart
                      cannot take the matrix or the other charts down with it. */}
                  <ChartErrorBoundary>
                    <EmissionsByRegionMap
                      data={regionQuery.data ?? null}
                      isLoading={regionQuery.isFetching}
                      error={
                        regionQuery.isError
                          ? regionQuery.error instanceof Error
                            ? regionQuery.error
                            : new Error(String(regionQuery.error))
                          : null
                      }
                      onRetry={regionQuery.refetch}
                    />
                  </ChartErrorBoundary>
                </div>

                {/* Order set by the product owner: the monthly total sits directly
                    under the map, with modality beneath it, so the two charts a
                    reader reaches for first are not below the per-customer pair. */}
                <div className="cpi-reports__charts-grid">
                  <EmissionsByMonthChart {...deriveChartProps(emissionsByMonthQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <IntensityByModalityChart {...deriveChartProps(intensityByModalityQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <EmissionsByCustomerChart {...deriveChartProps(emissionsByCustomerQuery)} />
                  <EmissionsByTocChart {...deriveChartProps(emissionsByTocQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <IntensityPerTonMonthlyChart {...deriveChartProps(intensityPerTonQuery)} />
                  <IntensityPerTonKmMonthlyChart {...deriveChartProps(intensityPerTonKmQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <IntensityByVehicleChart {...deriveChartProps(intensityByVehicleQuery)} />
                  <IntensityByOrderChart {...deriveChartProps(intensityByOrderQuery)} />
                </div>
              </div>
            )}

            {/* Operational Sub-Tab */}
            {dashboardSubTab === 'operational' && (
              <>
                <div className="cpi-reports__charts-grid">
                  <ErrorDistributionChart
                    {...deriveChartProps(errorDistributionQuery)}
                    onBarClick={(errorCategory) => {
                      const startStr = formatDateToString(startDate);
                      const endStr = formatDateToString(endDate);
                      navigate(`/data?tab=ingestion-issues&errorType=${encodeURIComponent(errorCategory)}&startDate=${startStr}&endDate=${endStr}`);
                    }}
                  />
                  <DataQualityByClientChart
                    {...deriveChartProps(dataQualityByClientQuery)}
                    sortOrder={dataQualitySortOrder}
                    onSortOrderChange={(order) => {
                      setDataQualitySortOrder(order);
                      if (cpiParams) {
                        queryClient.fetchQuery({
                          queryKey: dataQualityByClientQueryKey(cpiParams, order),
                          queryFn: async ({ signal }) => {
                            const auth: CpiAuth = await getSessionTokens(getSession);
                            return dataQualityByClientImpl(cpiParams, auth, signal, order);
                          },
                        }).catch(() => {});
                      }
                    }}
                  />
                </div>
                <div className="cpi-reports__charts-grid">
                  <ShipmentsByCustomerChart {...deriveChartProps(shipmentsByCustomerQuery)} />
                  <WeightByModalityChart {...deriveChartProps(weightByModalityQuery)} />
                </div>
              </>
            )}

            {/* Fuel Sub-Tab */}
            {dashboardSubTab === 'energy' && (
              <>
                <div className="cpi-reports__charts-grid">
                  <EmissionSourceDistributionChart {...deriveChartProps(emissionSourceDistributionQuery)} />
                  <EmissionsByFuelChart {...deriveChartProps(emissionsByFuelQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <FuelConsumptionTable {...deriveChartProps(fuelConsumptionQuery)} />
                  <FuelConsumptionChart {...deriveChartProps(fuelConsumptionQuery)} />
                </div>
                <div className="cpi-reports__charts-grid">
                  <AttributedFuelTable {...deriveChartProps(fuelConsumptionQuery)} />
                </div>
              </>
            )}

          </div>
        )}

        {/* Initial State - No filters applied */}
        {!showDashboardPanel && !showNoDataMessage && !error && (
          <div className="cpi-reports__initial-state">
            <div className="cpi-reports__initial-message">
              <span className="cpi-reports__initial-icon">📈</span>
              <h3>{t('cpi.getStarted')}</h3>
              <p>{t('cpi.getStartedMessage')}</p>
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default CpiReports;
